import * as React from "react"
import { Link, useLocation } from "react-router-dom"
import { ChevronRight, Home } from "lucide-react"
import { cn } from "@/lib/utils"

export interface BreadcrumbItem {
  label: string
  href?: string
}

export interface BreadcrumbProps extends React.HTMLAttributes<HTMLElement> {
  items?: BreadcrumbItem[]
  showHome?: boolean
  separator?: React.ReactNode
}

const Breadcrumb = React.forwardRef<HTMLElement, BreadcrumbProps>(
  ({ className, items, showHome = true, separator, ...props }, ref) => {
    const location = useLocation()
    
    // Auto-generate breadcrumbs from route if items not provided
    const breadcrumbItems = items || generateBreadcrumbsFromRoute(location.pathname)
    
    // Mobile: Show only current page + back button
    // Desktop: Show full breadcrumb trail
    
    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn("flex items-center", className)}
        {...props}
      >
        <ol className="flex items-center space-x-1 md:space-x-2 text-sm">
          {/* Home icon - always show on desktop, optional on mobile */}
          {showHome && (
            <li className="hidden md:flex items-center">
              <Link
                to="/dashboard"
                className="text-muted-foreground hover:text-foreground transition-colors flex items-center"
                aria-label="Home"
              >
                <Home className="h-4 w-4" />
              </Link>
            </li>
          )}
          
          {/* Breadcrumb items */}
          {breadcrumbItems.map((item, index) => {
            const isLast = index === breadcrumbItems.length - 1
            const Separator = separator || <ChevronRight className="h-4 w-4 text-muted-foreground" />
            
            return (
              <React.Fragment key={index}>
                {/* Separator */}
                {index > 0 && (
                  <li className="hidden md:flex items-center" aria-hidden="true">
                    {Separator}
                  </li>
                )}
                
                {/* Breadcrumb item */}
                <li className="flex items-center">
                  {isLast ? (
                    // Last item: current page (mobile shows only this)
                    <span
                      className={cn(
                        "font-medium text-foreground",
                        index === 0 && "md:hidden" // On mobile, show only if it's the first item
                      )}
                      aria-current="page"
                    >
                      {item.label}
                    </span>
                  ) : (
                    // Not last: link (desktop only)
                    <Link
                      to={item.href || '#'}
                      className="hidden md:flex text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              </React.Fragment>
            )
          })}
        </ol>
      </nav>
    )
  }
)
Breadcrumb.displayName = "Breadcrumb"

// Helper function to generate breadcrumbs from route path
function generateBreadcrumbsFromRoute(pathname: string): BreadcrumbItem[] {
  const pathMap: Record<string, string> = {
    '/dashboard': 'Dashboard',
    '/clients': 'Clients',
    '/posture-analysis': 'Posture Analysis',
    '/sop-assessment': 'SOP Assessment',
    '/drills': 'Drill Library',
    '/drill-planner': 'Drill Planner',
    '/chat': 'Ask Harry',
    '/progression-model': 'Progression Model',
    '/coaching': 'Coaching',
    '/settings': 'Settings',
  }
  
  const items: BreadcrumbItem[] = []
  const segments = pathname.split('/').filter(Boolean)
  
  // Add dashboard as home
  items.push({ label: 'Dashboard', href: '/dashboard' })
  
  // Build breadcrumbs from segments
  let currentPath = ''
  segments.forEach((segment, index) => {
    currentPath += `/${segment}`
    
    // Check if this is a known route
    if (pathMap[currentPath]) {
      items.push({
        label: pathMap[currentPath],
        href: currentPath,
      })
    } else if (index === segments.length - 1) {
      // Last segment might be a dynamic ID - show it as is
      items.push({
        label: segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
        href: currentPath,
      })
    }
  })
  
  return items
}

export { Breadcrumb }

